import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, message } from 'antd';
import {
  useGetFamilySharesQuery,
  useUpdateShareStatusMutation,
  useImportFamilyRelationMutation,
} from 'graphql/types';

export type FamilyShareItem = {
  key: string;
  type: 'outgoing' | 'incoming';
  status: string;
  partnerId: string;
  partnerName: string;
};

type UserInfo = {
  id?: string | null;
} | null;

export function useFamilyShareItems(userInfo: UserInfo) {
  const { data: shareResult, refetch: refetchShares, loading: shareLoading } = useGetFamilySharesQuery({});
  const [updateFamilyShareMutation] = useUpdateShareStatusMutation();
  const [importFamilyShareMutation] = useImportFamilyRelationMutation();
  const [selectedShareKey, setSelectedShareKey] = useState<string | null>(null);
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<{
    senderId?: string;
    senderName?: string;
    shareId?: string;
  } | null>(null);

  const shareItems = useMemo<FamilyShareItem[]>(() => {
    const shares = shareResult?.familyShares?.data || [];
    const result: FamilyShareItem[] = [];

    shares.forEach((item: any) => {
      const id = item?.id;
      const status = item?.attributes?.status || 'pending';
      const sender = item?.attributes?.requestor?.data?.attributes;
      const receiver = item?.attributes?.receiver?.data?.attributes;
      if (sender?.id === userInfo?.id || receiver?.id === userInfo?.id) {
        const isOutgoing = sender?.id === userInfo?.id;
        const partner = isOutgoing ? receiver : sender;
        result.push({
          key: id,
          type: isOutgoing ? 'outgoing' : 'incoming',
          status,
          partnerId: partner?.id,
          partnerName: partner?.username || 'Unknown',
        });
      }
    });
    return result;
  }, [shareResult, userInfo]);

  useEffect(() => {
    setSelectedShareKey((prev) => {
      if (prev && shareItems.some((s) => s.key === prev)) return prev;
      return shareItems[0]?.key || null;
    });
  }, [shareItems]);

  const selectedShare = useMemo(
    () => shareItems.find((s) => s.key === selectedShareKey) || null,
    [shareItems, selectedShareKey],
  );

  const incomingRequests = useMemo(() => {
    const shares = shareResult?.familyShares?.data || [];
    return shares
      .filter((item: any) => item?.attributes?.receiver?.data?.attributes?.id === userInfo?.id)
      .map((item: any) => ({
        shareId: item?.id,
        senderId: item?.attributes?.requestor?.data?.attributes?.id,
        senderName: item?.attributes?.requestor?.data?.attributes?.username,
      }));
  }, [shareResult, userInfo]);

  const actionInfo = useMemo(() => {
    if (!selectedShare) return { text: 'No selection', disabled: true };

    if (selectedShare.type === 'incoming') {
      return { text: 'Respond', disabled: false };
    }
    if (selectedShare.status === 'accepted') {
      return { text: 'Import Tree', disabled: false };
    }
    return { text: 'Cancel', disabled: false };
  }, [selectedShare]);

  const handleOpenDecisionModal = useCallback(() => {
    const target = incomingRequests[0] || null;
    setSelectedRequest(target);
    setDecisionModalOpen(true);
  }, [incomingRequests]);

  const handleAcceptShare = useCallback(async () => {
    try {
      await updateFamilyShareMutation({ variables: { id: selectedRequest?.shareId, status: 1 } });
      message.success(`Accepted sharing with ${selectedRequest?.senderName || 'user'}`);
      setDecisionModalOpen(false);
      await refetchShares();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  }, [selectedRequest, refetchShares, updateFamilyShareMutation]);

  const handleDeclineShare = useCallback(async () => {
    try {
      await updateFamilyShareMutation({ variables: { id: selectedRequest?.shareId, status: 2 } });
      message.info(`Declined sharing from ${selectedRequest?.senderName || 'user'}`);
      setDecisionModalOpen(false);
      await refetchShares();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  }, [selectedRequest, refetchShares, updateFamilyShareMutation]);

  const handleCancelShare = useCallback(
    (share: FamilyShareItem) => {
      Modal.confirm({
        title: 'Cancel sharing request',
        content: `Do you want to cancel the request to ${share.partnerName}?`,
        okText: 'Cancel Request',
        cancelText: 'Back',
        okButtonProps: { danger: true },
        centered: true,
        onOk: async () => {
          try {
            await updateFamilyShareMutation({ variables: { id: share.key, status: 3 } });
            message.success(`Cancelled request to ${share.partnerName}`);
            await refetchShares();
          } catch (err) {
            if (err instanceof Error) message.error(err.message);
            return Promise.reject(err);
          }
        },
      });
    },
    [refetchShares, updateFamilyShareMutation],
  );

  const handleImportShare = useCallback(
    (share: FamilyShareItem, treeNodeId: string) => {
      Modal.confirm({
        title: 'Import family tree',
        content: `Do you want to import ${share.partnerName}'s family tree into this node?`,
        okText: 'Import',
        cancelText: 'Back',
        centered: false,
        onOk: async () => {
          try {
            await importFamilyShareMutation({
              variables: { shareUserId: share.partnerId, treeNodeId },
            });
            message.success(`Imported sharing from ${share.partnerName}`);
          } catch (err) {
            if (err instanceof Error) message.error(err.message);
            return Promise.reject(err);
          }
        },
      });
    },
    [importFamilyShareMutation],
  );

  const shareMenu = {
    selectedKeys: selectedShareKey ? [selectedShareKey] : [],
    onClick: (info: { key: string }) => setSelectedShareKey(info.key),
    items: shareItems.map((s) => ({
      key: s.key,
      label: `${s.partnerName} (${s.status})`,
    })),
  };

  const selectedShareLabel = selectedShare
    ? `${selectedShare.partnerName} (${selectedShare.status})`
    : 'Select requestor';

  return {
    shareItems,
    shareLoading,
    selectedShare,
    selectedShareLabel,
    shareMenu,
    actionInfo,
    decisionModalOpen,
    setDecisionModalOpen,
    selectedRequest,
    handleOpenDecisionModal,
    handleAcceptShare,
    handleDeclineShare,
    handleCancelShare,
    handleImportShare,
    refetchShares,
  };
}
